// src/pages/SinglePage.tsx
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import Home from './Home.tsx';
import AboutUs from './AboutUs.tsx';
import Services from './Services.tsx';
import Projects from './Projects.tsx';
import News from './News.tsx';
import Contact from './Contact.tsx';
import './SinglePage.css';

// route path -> section id
const PATH_TO_SECTION: Record<string, string> = {
  '/': 'home',
  '/about-us': 'about-us',
  '/services': 'services',
  '/projects': 'projects',
  '/news': 'news',
  '/contact': 'contact',
};

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  // defer until layout stabilizes (images/fonts)
  requestAnimationFrame(() => {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

export default function SinglePage() {
  const location = useLocation();
  const firstPaintRef = useRef(true);

  // Disable browser scroll restoration to avoid fights with our manual scrolling
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'; // MDN
    }
  }, []);

  // Start at top only on the very first load of the app
  useLayoutEffect(() => {
    if (firstPaintRef.current) {
      firstPaintRef.current = false;
      window.scrollTo({ top: 0 });
    }
  }, []);

  // Scroll when the pathname changes (/, /services, etc.)
  useEffect(() => {
    const target = PATH_TO_SECTION[location.pathname] || 'home';
    const t = setTimeout(() => scrollToId(target), 0);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Also support hash targets (e.g. /services#services)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const t = setTimeout(() => scrollToId(id), 0);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <div>
      <section id="home" className="sp-section"><Home /></section>
      <section id="about-us" className="sp-section"><AboutUs /></section>
      <section id="services" className="sp-section"><Services /></section>
      <section id="projects" className="sp-section"><Projects /></section>
      <section id="news" className="sp-section"><News /></section>
      <section id="contact" className="sp-section"><Contact /></section>
    </div>
  );
}
